import { UI_PRIMITIVE__input } from '@/components/ui/UI_PRIMITIVE__input';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { Key } from '@/enums/key';
import { use_effect } from '@/hooks/_base/use_effect';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_generation, use_slice_ui, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_generation from '@/locales/keys/i18n_page_generation.json';
import { SEPARATOR__horizontal_dashed } from '@/pages/_base/separators/SEPARATOR__horizontal_dashed';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { CONSTANTS__generation_settings } from '@shared/constants/CONSTANTS__generation_settings';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD_PRIMITIVE__seed } from '@shared/dto/generation/graph_invoke/_fields/_primitives/FIELD_PRIMITIVE__seed';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PROPS {
  field: FIELD_PRIMITIVE__seed;
}
export function G_INPUT__seed({ field }: PROPS) {
  /*
   * STORE
   */
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_GENERATION);

  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);
  const { user_settings } = use_slice_user(['user_settings']);

  const [seed_component_id, set_seed_component_id] = useState('');
  const notifier = HOOK__notifier(seed_component_id, ENUM__workspace_entity.ENTITY_GENERATION);

  const [value_input, set_value_input] = useState<number | ''>('');

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (seed_component_id) void notifier.send_action();
  }, [seed_component_id]);

  /*
   * EVENTS
   */
  async function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (CONSTANTS__client.INPUT_POSITIVE_NUMBER_BLACKLIST_SYMBOLS.includes(event.key)) return event.preventDefault();

    if (event.key == Key.Escape) event.currentTarget.blur();
    if (event.key !== Key.Enter) return;

    let value_string = value_input;

    set_value_input('');

    if (value_string === '') return event.currentTarget.blur();

    let value = Number(value_string);

    value = Math.max(value, SEED_RANGE.min);
    value = Math.min(value, SEED_RANGE.max);

    const is_already_exists = field.get_value_range().some(item_saved => item_saved === value);
    if (is_already_exists) return;

    let field_new = field.clone();
    let value_range_new = field_new.get_value_range();
    value_range_new.push(value);
    field_new.set_value_range(value_range_new);

    //select new seed
    field_new.set_value(value);

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);

    set_seed_component_id(`${ENUM__component_type.PAGE_GENERATION_SEED_BAR}_${value}_${generation_current._id}`);
  }

  function onChange(event: React.ChangeEvent<HTMLInputElement>) {
    let value_string = event.target.value;

    let value_number = Number(value_string);

    value_number = Math.min(value_number, SEED_RANGE.max);

    if (/^[0-9]*$/.test(value_string) || value_string === '') {
      set_value_input(value_string === '' ? '' : value_number);
    }
  }

  /*
   * METHODS
   */

  /*
   * HTML
   */
  if (user_settings.generation_seeds_hide_input) return <></>;
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_bar_line />;

  const { SEED_RANGE } = CONSTANTS__generation_settings;

  return (
    <>
      <SEPARATOR__horizontal_dashed />
      <div className={`p-1 w-full bg-bar`}>
        <UI_PRIMITIVE__input
          value={value_input}
          className="w-full px-4 h-6 !text-xs"
          disabled={g_general__ui_disabled}
          type="number"
          placeholder={t(i18n_page_generation.sidebar_settings.setting.seed.input.placeholder)}
          min={SEED_RANGE.min}
          max={SEED_RANGE.max}
          {...{ onKeyDown, onChange }}
        />
      </div>
    </>
  );
}
